/**
 * PatternPrimitive.ts — кастомні примітиви для малювання патернів на графіку.
 *
 * Замість LineSeries (яка з'єднує всі свічки між точками і дає "брудні" лінії),
 * використовуємо ISeriesPrimitive API lightweight-charts v5:
 *   - series.attachPrimitive(primitive) — чіпляє до серії без нових series
 *   - draw(target: CanvasRenderingTarget2D) — малює прямо на canvas
 *   - Координати: timeToCoordinate + priceToCoordinate → множимо на pixelRatio
 */

import type { ISeriesPrimitive, IPrimitivePaneView, IPrimitivePaneRenderer, Time } from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

// ─── Типи ────────────────────────────────────────────────────────────────────

export interface DrawPoint {
    time: Time;
    value: number;
}

interface LineOptions {
    color: string;
    lineWidth: number;
    lineDash?: number[];
}

interface LabelOptions {
    text: string;
    color: string;
    offsetY?: number;   // зміщення по Y від точки (px в media)
    fontSize?: number;
}

// ─── Рендерер: полілінія ──────────────────────────────────────────────────────

class PolylineRenderer implements IPrimitivePaneRenderer {
    constructor(
        private readonly _points: DrawPoint[],
        private readonly _opts: LineOptions,
        private readonly _series: any,
        private readonly _timeScale: any,
    ) {}

    draw(target: CanvasRenderingTarget2D): void {
        target.useBitmapCoordinateSpace(scope => {
            const { context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr } = scope;

            const coords = this._points.map(p => {
                const x = this._timeScale.timeToCoordinate(p.time);
                const y = this._series.priceToCoordinate(p.value);
                if (x === null || y === null) return null;
                return { x: Math.round(x * hpr), y: Math.round(y * vpr) };
            }).filter(Boolean) as { x: number; y: number }[];

            if (coords.length < 2) return;

            ctx.save();
            ctx.beginPath();
            ctx.strokeStyle = this._opts.color;
            ctx.lineWidth = this._opts.lineWidth * hpr;
            ctx.lineJoin = 'round';
            if (this._opts.lineDash?.length) {
                ctx.setLineDash(this._opts.lineDash.map(d => d * hpr));
            }
            ctx.moveTo(coords[0].x, coords[0].y);
            for (let i = 1; i < coords.length; i++) {
                ctx.lineTo(coords[i].x, coords[i].y);
            }
            ctx.stroke();
            ctx.restore();
        });
    }
}

// ─── Рендерер: кружки у точках патерну ───────────────────────────────────────

class DotsRenderer implements IPrimitivePaneRenderer {
    constructor(
        private readonly _points: DrawPoint[],
        private readonly _color: string,
        private readonly _radius: number,   // media px
        private readonly _series: any,
        private readonly _timeScale: any,
    ) {}

    draw(target: CanvasRenderingTarget2D): void {
        target.useBitmapCoordinateSpace(scope => {
            const { context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr } = scope;

            ctx.save();
            ctx.fillStyle = this._color;

            for (const p of this._points) {
                const x = this._timeScale.timeToCoordinate(p.time);
                const y = this._series.priceToCoordinate(p.value);
                if (x === null || y === null) continue;
                ctx.beginPath();
                ctx.arc(x * hpr, y * vpr, this._radius * hpr, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.restore();
        });
    }
}

// ─── Рендерер: текстові підписи ───────────────────────────────────────────────

class LabelsRenderer implements IPrimitivePaneRenderer {
    constructor(
        private readonly _items: Array<{ point: DrawPoint; label: LabelOptions }>,
        private readonly _series: any,
        private readonly _timeScale: any,
    ) {}

    draw(target: CanvasRenderingTarget2D): void {
        target.useBitmapCoordinateSpace(scope => {
            const { context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr } = scope;

            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            for (const { point, label } of this._items) {
                const x = this._timeScale.timeToCoordinate(point.time);
                const y = this._series.priceToCoordinate(point.value);
                if (x === null || y === null) continue;

                const fontSize = (label.fontSize ?? 11) * hpr;
                const offsetY = (label.offsetY ?? 0) * vpr;
                const px = x * hpr;
                const py = y * vpr + offsetY;

                // Фон під текстом
                ctx.font = `bold ${fontSize}px Arial`;
                const tw = ctx.measureText(label.text).width;
                const pad = 2 * hpr;
                ctx.fillStyle = 'rgba(255,255,255,0.75)';
                ctx.fillRect(px - tw / 2 - pad, py - fontSize / 2 - pad, tw + pad * 2, fontSize + pad * 2);

                ctx.fillStyle = label.color;
                ctx.fillText(label.text, px, py);
            }
            ctx.restore();
        });
    }
}

// ─── View-обгортки (IPrimitivePaneView) ──────────────────────────────────────

class SimpleView implements IPrimitivePaneView {
    constructor(private readonly _renderer: IPrimitivePaneRenderer) {}
    renderer() { return this._renderer; }
    zOrder() { return 'normal' as const; }
}

// ─── Публічний клас-примітив ──────────────────────────────────────────────────

export interface PatternDrawOptions {
    /** Точки основної лінії патерну (shoulder/body line) */
    patternPoints: DrawPoint[];
    /** Точки другорядної лінії (neckline, handle тощо) */
    secondaryPoints?: DrawPoint[];
    /** Точки третьої лінії (resistance, target тощо) */
    tertiaryPoints?: DrawPoint[];
    /** Точки де малювати кружечки */
    dotPoints?: DrawPoint[];
    /** Текстові підписи */
    labels?: Array<{ point: DrawPoint; label: LabelOptions }>;

    patternColor: string;
    patternLineWidth?: number;
    secondaryColor?: string;
    secondaryDash?: number[];
    tertiaryColor?: string;
    tertiaryDash?: number[];
    dotColor?: string;
}

export class PatternPrimitive implements ISeriesPrimitive<Time> {
    private _opts: PatternDrawOptions;
    private _series: any = null;
    private _timeScale: any = null;
    private _views: IPrimitivePaneView[] = [];

    constructor(opts: PatternDrawOptions) {
        this._opts = opts;
    }

    attached({ series, chart }: any): void {
        this._series = series;
        this._timeScale = chart.timeScale();
        this._rebuild();
    }

    updateAllViews(): void {
        this._rebuild();
    }

    paneViews(): readonly IPrimitivePaneView[] {
        return this._views;
    }

    private _rebuild(): void {
        const views: IPrimitivePaneView[] = [];
        const { _opts: o, _series: s, _timeScale: ts } = this;

        // 1. Основна лінія патерну
        if (o.patternPoints.length >= 2) {
            views.push(new SimpleView(new PolylineRenderer(
                o.patternPoints,
                { color: o.patternColor, lineWidth: o.patternLineWidth ?? 2 },
                s, ts,
            )));
        }

        // 2. Другорядна лінія (neckline)
        if (o.secondaryPoints && o.secondaryPoints.length >= 2) {
            views.push(new SimpleView(new PolylineRenderer(
                o.secondaryPoints,
                {
                    color: o.secondaryColor ?? o.patternColor,
                    lineWidth: 1.5,
                    lineDash: o.secondaryDash ?? [6, 3],
                },
                s, ts,
            )));
        }

        // 3. Третя лінія (resistance тощо)
        if (o.tertiaryPoints && o.tertiaryPoints.length >= 2) {
            views.push(new SimpleView(new PolylineRenderer(
                o.tertiaryPoints,
                {
                    color: o.tertiaryColor ?? o.patternColor,
                    lineWidth: 1,
                    lineDash: o.tertiaryDash ?? [5, 5],
                },
                s, ts,
            )));
        }

        // 4. Кружечки у ключових точках
        if (o.dotPoints?.length) {
            views.push(new SimpleView(new DotsRenderer(
                o.dotPoints,
                o.dotColor ?? o.patternColor,
                3,
                s, ts,
            )));
        }

        // 5. Текстові підписи
        if (o.labels?.length) {
            views.push(new SimpleView(new LabelsRenderer(o.labels, s, ts)));
        }

        this._views = views;
    }
}
